import React, { useEffect, useMemo, useState } from 'react';
import TextareaAutosize from 'react-textarea-autosize';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { NumericFormat } from 'react-number-format';
import LabeledField from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';
import { ProductTypeTaxItem, getProductTypeTax } from '@/modules/settings/redux/SettingsCRUD';
import { Document, uploadDocument } from '@/modules/documents/redux/DocumentCRUD';

interface OrderItem {
  id: number;
  product_id?: string;
  product_code?: string;
  product_name: string;
  item_type_tax: string;
  color: string;
  quantity: number;
  unit?: {
    id: string;
    name: string;
  } | null;
  unit_id: string;
  unit_name: string;
  unit_price: number;
  total_price_not_tax: number;
  total_price: number;
  notes?: string;
  isGroup?: boolean;
  groupName?: string;
}

interface OrderItemEditModalProps {
  open: boolean;
  item: OrderItem | null;
  units: Array<{ id: string; name: string; code: string }>;
  productTypes: ProductTypeTaxItem[];
  disabled?: boolean;
  isCreate?: boolean;
  orderItemContentTypeId?: number | null;
  itemDocuments?: Document[];
  isLoadingItemDocuments?: boolean;
  onClose: () => void;
  onItemChange: (itemId: number, field: keyof OrderItem, value: any) => void;
  onUploadFiles?: (itemId: number, files: File[]) => void | Promise<void>;
  onCreateItem?: (item: OrderItem) => void;
  onReloadItemDocuments?: (itemId: number) => Promise<void> | void;
  onDeleteItemDocument?: (itemId: number, documentId: string) => Promise<void> | void;
}

const LABEL_WIDTH = 110;

const parseNumericInput = (rawValue: string, thousandSep: string, decimalSep: string): number => {
  if (!rawValue) return 0;

  const decimalIndex = decimalSep ? rawValue.lastIndexOf(decimalSep) : -1;
  let integerPart = decimalIndex !== -1 ? rawValue.substring(0, decimalIndex) : rawValue;
  const decimalPart = decimalIndex !== -1 ? rawValue.substring(decimalIndex + 1) : '';

  if (thousandSep) {
    const escaped = thousandSep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    integerPart = integerPart.replace(new RegExp(escaped, 'g'), '');
  }

  const cleaned = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const parseVietnameseNumber = (value: unknown): number => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  if (typeof value !== 'string') {
    return 0;
  }

  return parseNumericInput(value.trim(), '.', ',');
};

const normalizeOrderItemNumbers = (orderItem: OrderItem): OrderItem => ({
  ...orderItem,
  quantity: parseVietnameseNumber(orderItem.quantity),
  unit_price: parseVietnameseNumber(orderItem.unit_price),
  total_price: parseVietnameseNumber(orderItem.total_price),
  total_price_not_tax: parseVietnameseNumber(orderItem.total_price_not_tax),
});

const getTotals = (
  quantity: number,
  unitPrice: number,
  itemTypeTax: string,
  productTypes: ProductTypeTaxItem[]
) => {
  const totalPrice = unitPrice * quantity;
  const productType = productTypes.find((pt) => pt.id === itemTypeTax);
  const taxRate = productType ? productType.value : 0;

  return {
    total_price: totalPrice,
    total_price_not_tax: totalPrice - (totalPrice * taxRate) / 100,
  };
};

export default function OrderItemEditModal({
  open,
  item,
  units,
  productTypes,
  disabled = false,
  isCreate = false,
  orderItemContentTypeId = null,
  itemDocuments = [],
  isLoadingItemDocuments = false,
  onClose,
  onItemChange,
  onUploadFiles,
  onCreateItem,
  onReloadItemDocuments,
  onDeleteItemDocument,
}: OrderItemEditModalProps) {
  const [form, setForm] = useState<OrderItem | null>(item ? normalizeOrderItemNumbers(item) : null);
  const uploadInputRef = React.useRef<HTMLInputElement | null>(null);
  const [localProductTypes, setLocalProductTypes] = useState<ProductTypeTaxItem[]>([]);

  useEffect(() => {
    getProductTypeTax()
      .then((data) => setLocalProductTypes(data.filter((pt) => pt.is_active === true)))
      .catch(() => {});
  }, []);

  const resolvedProductTypes = useMemo(
    () => (localProductTypes.length > 0 ? localProductTypes : productTypes),
    [localProductTypes, productTypes]
  );

  useEffect(() => {
    const normalizedItem = item ? normalizeOrderItemNumbers(item) : null;

    if (item && !item.item_type_tax && resolvedProductTypes.length > 0) {
      setForm({ ...normalizedItem!, item_type_tax: String(resolvedProductTypes[0].id) });
    } else {
      setForm(normalizedItem);
    }
  }, [item]);

  const selectedUnitOption = useMemo(() => {
    if (!form) return null;

    const normalizedUnitId = String(form.unit?.id || form.unit_id || '');
    const normalizedUnitName = form.unit?.name || form.unit_name || '';
    const selectedUnitFromOptions =
      units.find((unit) => String(unit.id) === normalizedUnitId) ||
      units.find((unit) => unit.name === normalizedUnitName) ||
      null;

    return selectedUnitFromOptions ||
      (normalizedUnitId || normalizedUnitName
        ? {
            id: normalizedUnitId,
            name: normalizedUnitName || normalizedUnitId,
            code: normalizedUnitName || normalizedUnitId,
          }
        : null);
  }, [form, units]);

  const unitOptions = useMemo(() => {
    if (!form) return units;

    const normalizedUnitId = String(form.unit?.id || form.unit_id || '');
    const normalizedUnitName = form.unit?.name || form.unit_name || '';
    const selectedUnitFromOptions =
      units.find((unit) => String(unit.id) === normalizedUnitId) ||
      units.find((unit) => unit.name === normalizedUnitName) ||
      null;

    if (selectedUnitFromOptions || (!normalizedUnitId && !normalizedUnitName)) {
      return units;
    }

    return [
      ...units,
      {
        id: normalizedUnitId,
        name: normalizedUnitName || normalizedUnitId,
        code: normalizedUnitName || normalizedUnitId,
      },
    ];
  }, [form, units]);

  const updateForm = (field: keyof OrderItem, value: any) => {
    setForm((prev) => {
      if (!prev) return prev;

      const next = { ...prev, [field]: value };

      if (field === 'quantity' || field === 'unit_price' || field === 'item_type_tax') {
        const totals = getTotals(
          field === 'quantity' ? value : next.quantity,
          field === 'unit_price' ? value : next.unit_price,
          field === 'item_type_tax' ? value : next.item_type_tax,
          resolvedProductTypes
        );
        next.total_price = totals.total_price;
        next.total_price_not_tax = totals.total_price_not_tax;
      }

      return next;
    });
  };

  const handleSave = () => {
    if (!form || disabled) {
      onClose();
      return;
    }

    if (isCreate && onCreateItem) {
      onCreateItem({ ...form });
      onClose();
      return;
    }

    if (form.isGroup) {
      onItemChange(form.id, 'groupName', form.groupName || '');
      onClose();
      return;
    }

    ([
      'product_id',
      'product_code',
      'product_name',
      'color',
      'quantity',
      'unit',
      'unit_id',
      'unit_name',
      'unit_price',
      'total_price',
      'item_type_tax',
      'total_price_not_tax',
      'notes',
    ] as Array<keyof OrderItem>).forEach((field) => {
      onItemChange(form.id, field, form[field]);
    });

    onClose();
  };

  const handleUploadFiles = React.useCallback(async (files: FileList | null) => {
    if (!form) {
      return;
    }

    const selectedFiles = Array.from(files || []);
    if (selectedFiles.length === 0) {
      return;
    }

    try {
      if (onUploadFiles) {
        await onUploadFiles(form.id, selectedFiles);
      } else {
        if (!orderItemContentTypeId) {
          return;
        }

        await Promise.all(selectedFiles.map(async (file) => {
          await uploadDocument({
            content_type_id: orderItemContentTypeId,
            object_id: String(form.id),
            name: file.name,
            file,
            mime_type: file.type || 'application/octet-stream',
          });
        }));
      }
    } finally {
      await onReloadItemDocuments?.(form.id);
    }
  }, [form, onUploadFiles, orderItemContentTypeId, onReloadItemDocuments]);

  if (!form) {
    return null;
  }

  const getTitle = () => {
    if (isCreate) return form.isGroup ? 'Thêm phân vùng' : 'Thêm sản phẩm';
    return form.isGroup ? 'Sửa phân vùng' : 'Sửa sản phẩm';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        {getTitle()}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 2, bgcolor: '#f9fafb' }}>

          {form.isGroup ? (
            <LabeledField label="Tên phân vùng" required labelWidth={LABEL_WIDTH} >
              <TextField
                fullWidth
                size="small"
                value={form.groupName || ''}
                onChange={(event) => updateForm('groupName', event.target.value)}
                disabled={disabled}
                autoComplete="off"
                sx={{
                  '& .MuiInputBase-input': { fontWeight: 400, color: '#374151' },
                  '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#374151', color: '#374151', opacity: 1 },
                }}
              />
            </LabeledField>
          ) : (
            <Grid container rowSpacing={1} columnSpacing={5}>
              <Grid size={{ xs: 12 }}>
                <LabeledField label="Tên hàng quy cách" required labelWidth={LABEL_WIDTH} alignItems="start">
                  <TextareaAutosize
                    minRows={3}
                    maxRows={8}
                    value={form.product_name || ''}
                    onChange={(event) => updateForm('product_name', event.target.value)}
                    disabled={disabled}
                    style={sharedFormStyles.additionalTextarea(!disabled)}
                  />
                </LabeledField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <LabeledField label="Màu sắc" labelWidth={LABEL_WIDTH} alignItems="start">
                  <TextareaAutosize
                    minRows={2}
                    maxRows={6}
                    value={form.color || ''}
                    onChange={(event) => updateForm('color', event.target.value)}
                    disabled={disabled}
                    style={sharedFormStyles.additionalTextarea(!disabled)}
                  />
                </LabeledField>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                  <LabeledField label="Số lượng" required labelWidth={LABEL_WIDTH} focusCursorPosition="default">
                  <NumericFormat
                    value={form.quantity}
                    onValueChange={({ floatValue }) => updateForm('quantity', floatValue ?? 0)}
                    decimalScale={3}
                    thousandSeparator="."
                    decimalSeparator=","
                    customInput={TextField}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    sx={{
                      '& .MuiInputBase-input': { fontWeight: 400, color: '#374151', textAlign: 'right' },
                      '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#374151', color: '#374151', opacity: 1 },
                    }}
                  />
                </LabeledField>

                <LabeledField label="ĐVT" required labelWidth={LABEL_WIDTH}>
                  <Autocomplete
                    fullWidth
                    options={unitOptions}
                    value={selectedUnitOption}
                    onChange={(_event, newValue) => {
                      updateForm('unit', newValue ? { id: String(newValue.id || ''), name: newValue.name || '' } : null);
                      updateForm('unit_id', String(newValue?.id || ''));
                      updateForm('unit_name', newValue?.name || '');
                    }}
                    size="small"
                    disabled={disabled}
                    getOptionLabel={(option: any) => {
                      if (!option) return '';
                      return option.name || '';
                    }}
                    isOptionEqualToValue={(option: any, value: any) => option?.id === value?.id}
                    filterOptions={(options, { inputValue }) => {
                      return options.filter((option: any) => {
                        const name = option.name?.toLowerCase() || '';
                        const code = option.code?.toLowerCase() || '';
                        const searchTerm = inputValue.toLowerCase();
                        return name.includes(searchTerm) || code.includes(searchTerm);
                      });
                    }}
                    noOptionsText="Không tìm thấy đơn vị tính"
                    clearOnEscape
                    selectOnFocus
                    handleHomeEndKeys
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        placeholder="Chọn ĐVT"
                        sx={{
                          '& .MuiInputBase-input': { textAlign: 'right' },
                          '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#374151', color: '#374151', opacity: 1 },
                        }}
                      />
                    )}
                  />
                </LabeledField>

                <LabeledField label="Đơn giá" required labelWidth={LABEL_WIDTH} focusCursorPosition="default">
                  <NumericFormat
                    value={form.unit_price}
                    onChange={(event) => updateForm('unit_price', parseNumericInput(event.target.value, '.', ','))}
                    decimalScale={0}
                    thousandSeparator="."
                    decimalSeparator=","
                    customInput={TextField}
                    size="small"
                    fullWidth
                    disabled={disabled}
                    sx={{
                      '& .MuiInputBase-input': { fontWeight: 400, color: '#374151', textAlign: 'right' },
                      '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#374151', color: '#374151', opacity: 1 },
                    }}
                  />
                </LabeledField>

               
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <LabeledField label="Thành tiền" labelWidth={LABEL_WIDTH} focusCursorPosition="default">
                  <NumericFormat
                    value={form.total_price}
                    decimalScale={0}
                    thousandSeparator="."
                    decimalSeparator=","
                    customInput={TextField}
                    size="small"
                    fullWidth
                    disabled
                    sx={{
                      '& .MuiInputBase-input': { fontWeight: 400, color: '#374151', textAlign: 'right' },
                      '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#374151', color: '#374151', opacity: 1 },
                    }}
                  />
                </LabeledField>

                <LabeledField label="Chưa VAT" labelWidth={LABEL_WIDTH} focusCursorPosition="default">
                  <NumericFormat
                    value={form.total_price_not_tax}
                    decimalScale={0}
                    thousandSeparator="."
                    decimalSeparator=","
                    customInput={TextField}
                    size="small"
                    fullWidth
                    disabled
                    sx={{
                      '& .MuiInputBase-input': { fontWeight: 400, color: '#374151', textAlign: 'right' },
                      '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: '#374151', color: '#374151', opacity: 1 },
                    }}
                  />
                </LabeledField>

                  <LabeledField label="Phân loại" labelWidth={LABEL_WIDTH}>
                  <FormControl fullWidth size="small">
                    <Select
                      value={form.item_type_tax || ''}
                      onChange={(event) => updateForm('item_type_tax', event.target.value)}
                      displayEmpty
                      disabled={disabled}
                      sx={{
                        ...sharedFormStyles.inputArrowAlign,
                        '& .MuiSelect-select': { fontWeight: 400, color: '#374151' },
                        '& .MuiSelect-select.Mui-disabled': { WebkitTextFillColor: '#374151', color: '#374151', opacity: 1 },
                      }}
                    >
                      <MenuItem value="">
                        <em>Chọn loại</em>
                      </MenuItem>
                      {resolvedProductTypes.map((pt) => (
                        <MenuItem key={pt.id || pt.name} value={pt.id}>
                          {pt.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </LabeledField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <LabeledField label="Ghi chú" labelWidth={LABEL_WIDTH} alignItems="start">
                  <TextareaAutosize
                    minRows={2}
                    maxRows={6}
                    value={form.notes || ''}
                    onChange={(event) => updateForm('notes', event.target.value)}
                    disabled={disabled}
                    style={sharedFormStyles.additionalTextarea(!disabled)}
                  />
                </LabeledField>
              </Grid>

              <Grid size={{ xs: 12 }}>
                <LabeledField label="Hình ảnh" labelWidth={LABEL_WIDTH} alignItems="start">
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
                    <input
                      ref={(node) => {
                        uploadInputRef.current = node;
                      }}
                      type="file"
                      hidden
                      multiple
                      accept="image/*,.txt,.png,.jpg,.jpeg,.pdf,.doc,.docx,.zip,.rar,.mp4,.mp3,.avi,.mov,.gif,.bmp,.tiff,.svg,.webp"
                      onChange={(event) => {
                        if (disabled) {
                          event.target.value = '';
                          return;
                        }

                        void handleUploadFiles(event.target.files);
                        event.target.value = '';
                      }}
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title="Upload hình ảnh hoặc file">
                        <span>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<CloudUploadIcon fontSize="small" />}
                            disabled={disabled}
                            onClick={() => uploadInputRef.current?.click()}
                            sx={sharedFormStyles.secondaryButton}
                          >
                            Tải lên
                          </Button>
                        </span>
                      </Tooltip>

                      {isLoadingItemDocuments && (
                        <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          Đang tải file...
                        </Typography>
                      )}
                    </Box>

                    {itemDocuments.length > 0 && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {itemDocuments.map((doc) => (
                          <Box
                            key={doc.id}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              minWidth: 0,
                              border: '1px solid #e5e7eb',
                              borderRadius: '4px',
                              px: 1,
                              py: 0.5,
                              backgroundColor: '#fff',
                            }}
                          >
                            <AttachFileIcon sx={{ fontSize: 16, color: '#6b7280', flexShrink: 0 }} />
                            <a
                              href={doc.file_url || '#'}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(event) => {
                                if (!doc.file_url) {
                                  event.preventDefault();
                                }
                              }}
                              style={{
                                color: '#1d4ed8',
                                fontSize: '12px',
                                textDecoration: 'none',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                                flex: 1,
                              }}
                              title={doc.name}
                            >
                              {doc.name}
                            </a>
                            <IconButton
                              size="small"
                              title="Xóa file"
                              disabled={disabled}
                              onClick={() => {
                                if (disabled || !form) {
                                  return;
                                }
                                void onDeleteItemDocument?.(form.id, doc.id);
                              }}
                              sx={{
                                p: 0,
                                width: 20,
                                height: 20,
                                minWidth: 20,
                                borderRadius: '999px',
                                border: '1px solid #fca5a5',
                                backgroundColor: '#fef2f2',
                                color: '#dc2626',
                                fontSize: '12px',
                                fontWeight: 700,
                                opacity: disabled ? 0.5 : 1,
                                '&:hover': {
                                  backgroundColor: disabled ? '#fef2f2' : '#fee2e2',
                                },
                              }}
                            >
                              x
                            </IconButton>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>
                </LabeledField>
              </Grid>
            </Grid>
          )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} variant="contained" size="small" sx={sharedFormStyles.cancelButton}>
          Hủy
        </Button>
        <Button onClick={handleSave} variant="contained" size="small" disabled={disabled} sx={sharedFormStyles.primaryButton}>
          Lưu
        </Button>
      </DialogActions>
    </Dialog>
  );
}
